import { lerp } from '@/utils/math';
import { EventEmitter } from 'events';
const VirtualScroll = require('virtual-scroll');

class SmoothScrolling {
  public started: boolean = false
  public ee: EventEmitter = new EventEmitter()
  private ease: number = 0.1
  private currentY: number = 0          // Current Y position.
  private targetY: number = 0           // Target Y position requested.
  private maxY: number = 0              // Max Y scroll. Used to clamp scroll to last content.
  private wrapper?: HTMLElement
  private content?: HTMLElement
  private contentFirst?: HTMLElement    // First content used to dynamically define nav menu max scroll position.
  private contentLast?: HTMLElement     // Last content used to dynamically set
  private nav?: HTMLElement
  private rAF?: number
  private VS: any = new VirtualScroll({ useKeyboard: false })

  constructor() {
    // Binds.
    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.run = this.run.bind(this);
  }

  /**
   * Set content wrapper style.
   */
  private setStyles(): void {
    this.wrapper!.style.position = 'fixed';
    this.wrapper!.style.top = '0';
    this.wrapper!.style.left = '0';
    this.wrapper!.style.height = '100vh';
    this.wrapper!.style.width = '100vw';
    this.wrapper!.style.overflow = 'hidden';
  }

  /**
   * Set body height.
   */
  private setSizes(): void {
    // Update body height.
    document.body.style.height = `${this.content!.offsetHeight}px`;
    // Update max scroll.
    this.maxY = this.contentLast!.offsetParent!.getBoundingClientRect().top - this.contentLast!.getBoundingClientRect().top;
  }

  /**
   * On window resize.
   */
  private onResize(): void {
    this.setSizes();
  }

  /**
   * On scroll.
   *
   * @param e
   */
  private onScroll(e: { deltaX: number, deltaY: number }): void {
    this.targetY += e.deltaY;

    // Clamp target value to not scroll to far nor to low.
    this.targetY = Math.max(this.maxY, this.targetY);
    this.targetY = Math.min(0, this.targetY);
  }

  /**
   * Init smooth scrolling.
   */
  private async init(): Promise<void> {
    // Await for nodes to be loaded.
    while (!document.getElementById('ss-wrapper')
      || !document.getElementById('ss-content')
      || !document.querySelector('#ss-content section[data-content-role="first"]')
      || !document.querySelector('#ss-content section[data-content-role="last"]')
      || !document.getElementById('ss-nav')
    ) {
      await new Promise(r => setTimeout(r, 10));
    }

    // Get all nodes.
    this.wrapper = document.getElementById('ss-wrapper')!;
    this.content = document.getElementById('ss-content')!;
    this.contentFirst = document.querySelector('#ss-content section[data-content-role="first"]')! as HTMLElement;
    this.contentLast = document.querySelector('#ss-content section[data-content-role="last"]')! as HTMLElement;
    this.nav = document.getElementById('ss-nav')!;

    // Set size data.
    this.setSizes();

    // Events.
    window.addEventListener('resize', this.onResize, { passive: true });
    this.VS.on(this.onScroll);
  }

  /**
   * When for instance to be started.
   */
  private async whenStarted(): Promise<void> {
    while (!this.started) await new Promise(r => setTimeout(r, 10));
  }

  /**
   * Smooth scrolling effect.
   */
  private run(): void {
    const previousCurrentY = this.currentY;

    // Set new current position.
    this.currentY = lerp(this.currentY, this.targetY, this.ease);
    this.currentY = Math.floor(this.currentY * 100) / 100;

    // Move content. Nav menu must be fixed on top.
    this.content!.style.transform = `translate3d(0, ${this.currentY}px, 0)`;
    this.nav!.style.transform = `translate3d(0, ${Math.max(-this.contentFirst!.getBoundingClientRect().height, this.currentY)}px, 0)`;

    if (previousCurrentY * 100 !== this.currentY * 100) this.ee.emit('scroll', this.currentY);

    // Update on next animation frame.
    this.rAF = requestAnimationFrame(this.run);
  }

  /**
   * Start smooth scrolling.
   */
  private on(): void {
    this.setSizes();
    this.setStyles();

    this.rAF = requestAnimationFrame(this.run);
  }

  /**
   * Start SmoothScrolling.
   */
  public async start(): Promise<void> {
    await this.init();

    this.started = true;

    this.on();
  }

  /**
   * Scroll to content.
   *
   * @param id Id of div to scroll to.
   * @param animate (optional) Animate scroll (default `true`)
   */
  public async scrollTo(id: string, animate: boolean = true): Promise<void> {
    await this.whenStarted();

    const node = document.getElementById(id);

    if (node) {
      // Set new scroll position.
      this.targetY = this.content!.getBoundingClientRect().top - node.getBoundingClientRect().top;

      if (!animate) this.currentY = this.targetY;
    }
  }
}

export default new SmoothScrolling();
